import { useCallback, useEffect, useState } from "react";
import { Box } from "~/components/Box";
import { Card, CardBody } from "~/components/Card";
import {
  Card as CalendarCard,
  CardBody as CalendarCardBody,
  CardFooter,
} from "~/core/modules/DeprecatedBooking/components/Card";
import { Flex } from "~/components/Flex";
import { FieldLabel } from "~/components/FormControl";
import { Icon } from "~/components/Icon";
import { SvgCalendar, SvgFilter } from "~/components/Icon/icons";
import { TextInput } from "~/components/Input";
import { Text } from "~/components/Text";
import { Button } from "~/components/Button";
import { DoubleCalendar } from "~/core/shared/components/DoubleCalendar";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "~/core/shared/components/Popover";
import { dayjs } from "~/utils/date.utils";
import { TravelAnalyticsPageFilters } from "../types";
import { currentYearMonth, monthsAgoYearMonth, startOfYearYearMonth } from "../utils";

const YEAR_MONTH_FORMAT = "YYYY-MM";

interface PeriodPreset {
  label: string;
  from: () => string;
  to: () => string;
}

// The dashboard filters by month (`YYYY-MM`), so week granularity collapses
// to the current month.
const PERIOD_PRESETS: PeriodPreset[] = [
  { label: "Este mês", from: currentYearMonth, to: currentYearMonth },
  { label: "Últimos 3 meses", from: () => monthsAgoYearMonth(2), to: currentYearMonth },
  { label: "Últimos 6 meses", from: () => monthsAgoYearMonth(5), to: currentYearMonth },
  { label: "Este ano", from: startOfYearYearMonth, to: currentYearMonth },
  { label: "Últimos 12 meses", from: () => monthsAgoYearMonth(11), to: currentYearMonth },
];

const yearMonthToDate = (yearMonth: string): Date =>
  dayjs(yearMonth, YEAR_MONTH_FORMAT).startOf("month").toDate();

const dateToYearMonth = (date: Date): string => dayjs(date).format(YEAR_MONTH_FORMAT);

const displayYearMonth = (yearMonth: string): string =>
  dayjs(yearMonth, YEAR_MONTH_FORMAT).format("MMM/YYYY");

export interface FiltersProps {
  filters: TravelAnalyticsPageFilters;
  onChange: (filters: TravelAnalyticsPageFilters) => void;
}

export const Filters = ({ filters, onChange }: FiltersProps) => {
  // Local draft state — picking dates updates the draft only. The committed
  // `filters` (and therefore the dashboard queries) change only on "Aplicar".
  const [draftFrom, setDraftFrom] = useState(filters.fromYearMonth);
  const [draftTo, setDraftTo] = useState(filters.toYearMonth);

  // Keep the draft in sync when `filters` changes externally.
  useEffect(() => {
    setDraftFrom(filters.fromYearMonth);
    setDraftTo(filters.toYearMonth);
  }, [filters.fromYearMonth, filters.toYearMonth]);

  const handleDateClick = useCallback(
    (date: Date) => {
      const yearMonth = dateToYearMonth(date);

      if (!draftFrom) {
        setDraftFrom(yearMonth);
        return;
      }

      if (!draftTo) {
        if (yearMonth < draftFrom) {
          setDraftFrom(yearMonth);
        } else {
          setDraftTo(yearMonth);
        }
        return;
      }

      setDraftFrom(yearMonth);
      setDraftTo("");
    },
    [draftFrom, draftTo],
  );

  const handlePresetClick = useCallback(
    (preset: PeriodPreset) => {
      // Presets apply immediately — commit the filter right away.
      const from = preset.from();
      const to = preset.to();
      setDraftFrom(from);
      setDraftTo(to);
      onChange({ ...filters, fromYearMonth: from, toYearMonth: to });
    },
    [filters, onChange],
  );

  const handleApply = useCallback(() => {
    if (draftFrom && !draftTo) {
      // Partial range: user picked only one month — commit it as a single month.
      onChange({ ...filters, fromYearMonth: draftFrom, toYearMonth: draftFrom });
      return;
    }

    onChange({ ...filters, fromYearMonth: draftFrom, toYearMonth: draftTo });
  }, [draftFrom, draftTo, filters, onChange]);

  return (
    <Card css={{ mb: "$6" }}>
      <CardBody css={{ p: "$6" }}>
        <Flex align="center" gap="2" css={{ mb: "$4" }}>
          <Icon as={SvgFilter} />
          <Text size="3" css={{ fw: "600" }}>
            Filtros
          </Text>
        </Flex>

        <Flex
          gap="4"
          align="end"
          wrap="wrap"
          css={{ "@mxlg": { flexDirection: "column", alignItems: "stretch" } }}
        >
          <Popover>
            <Box css={{ minWidth: "180px" }}>
              <FieldLabel>De</FieldLabel>
              <PopoverTrigger asChild>
                <TextInput
                  size="smlg"
                  readOnly
                  leftIcon={SvgCalendar}
                  placeholder="Selecione o mês"
                  value={draftFrom ? displayYearMonth(draftFrom) : ""}
                  style={{ width: "100%" }}
                  onChange={() => null}
                  onBlur={() => null}
                />
              </PopoverTrigger>
            </Box>

            <Box css={{ minWidth: "180px" }}>
              <FieldLabel>Até</FieldLabel>
              <PopoverTrigger asChild>
                <TextInput
                  size="smlg"
                  readOnly
                  leftIcon={SvgCalendar}
                  placeholder="Selecione o mês"
                  value={draftTo ? displayYearMonth(draftTo) : ""}
                  style={{ width: "100%" }}
                  onChange={() => null}
                  onBlur={() => null}
                />
              </PopoverTrigger>
            </Box>

            <Box css={{ "@mxlg": { width: "100%" } }}>
              <FieldLabel>Períodos rápidos</FieldLabel>
              <Flex gap="2" wrap="wrap">
                {PERIOD_PRESETS.map((preset) => {
                  const isActive = draftFrom === preset.from() && draftTo === preset.to();

                  return (
                    <Button
                      key={preset.label}
                      type="button"
                      size="sm"
                      rounded
                      variant={isActive ? "primary" : "tertiary"}
                      data-active={isActive}
                      onClick={() => handlePresetClick(preset)}
                    >
                      <Text size="2">{preset.label}</Text>
                    </Button>
                  );
                })}
              </Flex>
            </Box>

            <PopoverContent align="center">
              <CalendarCard css={{ position: "absolute" }}>
                <DoubleCalendar
                  date={yearMonthToDate(draftFrom)}
                  activeMinDate={draftFrom ? yearMonthToDate(draftFrom) : undefined}
                  activeMaxDate={draftTo ? yearMonthToDate(draftTo) : undefined}
                  onDateClick={handleDateClick}
                />
                <CardFooter>
                  <CalendarCardBody css={{ p: "$4" }}>
                    <Flex css={{ justifyContent: "flex-end" }}>
                      <PopoverClose asChild>
                        <Button variant="secondary" onClick={handleApply}>
                          <Text>Aplicar</Text>
                        </Button>
                      </PopoverClose>
                    </Flex>
                  </CalendarCardBody>
                </CardFooter>
              </CalendarCard>
            </PopoverContent>
          </Popover>
        </Flex>
      </CardBody>
    </Card>
  );
};

Filters.displayName = "Filters";
